import {
  classifyUrl,
  detectMediaColumns,
  extractUrls,
  mediaColumnsTakenOver,
} from '@/lib/utils/image-url-detection';

export const smartOfferValues = {
  name: ['NOM', 'NAME', 'NOM DU PRODUIT'],
  optionColor: ['COULEUR', 'Couleur', 'COLOR', 'COLOUR'],
  optionSize: ['TAILLE', 'Taille', 'SIZE'],
  optionOther: ['AUTRE', 'OTHER'],
  url: ['URL'],
  gtin: ['GTIN', 'VARIANT', 'ID SHOPIFY', 'ID PRODUIT SHOPIFY', 'VAR ID'],
  sku: ['SKU', 'REF PRODUIT'],
  description: ['DESCRIPTION', 'Description'],
  gender: ['GENRE', 'GENDER'],
  stock: ['STOCK', 'Stock'],
  deliveryDelays: ['DELAIS', 'DELIVERY'],
  isReturnable: ['RETOUR', 'IS RETURNABLE'],
  shippingPrice: [
    'FRAIS DE PORT À DOMICILE',
    'Frais de port à domicile',
    'FRAIS DE PORT',
    'Frais de port',
    'FRAIS DE LIVRAISON',
    'SHIPPING',
    'Shipping Price',
  ],
  weight: ['POIDS', 'WEIGHT'],
  buyingPrice: [
    'PRIX ACHAT',
    'BUYING PRICE',
    'RETROCESSION',
    'PRIX DE RETROCESSION HT',
    'Prix de rétrocession ht',
    'BTOB PRICE',
    'PRIX B2B',
    'B2B PRICE',
  ],
  retailPrice: [
    'PRIX PUBLIC',
    'PRIX PUBLIC CONSEILLE TTC',
    'Prix public conseillé ttc',
    'RETAIL PRICE',
    'PUBLIC PRICE',
  ],
  choosePrice: [
    'PRIX CHOOSE',
    'PRIX APRES REMISE TTC',
    'Prix après remise ttc',
    'PRIX CHOOSE TTC RECOMMANDE',
    'Prix choose ttc recommandé',
    'CHOOSE PRICE',
    'CHOOSEPRICE',
  ],
  choosePriceEEE: [
    'PRIX CHOOSE EEE',
    'ECO-PART',
    'ECO-TAXE',
    'Éco-Taxe',
    'ECO PART',
    'ECO TAXE',
    'DEEE',
  ],
  chooseMargin: ['MARGE CHOOSE', 'MARGE', 'Marge', 'CHOOSE MARGIN'],
  originCountry: [
    'PAYS',
    'PAYS DE FABRICATION',
    'Pays de fabrication',
    'COUNTRY',
    'Origin Country',
  ],
  summary: ['RESUME', 'SUMMARY'],
  cgu: ['CGU'],
  faqBeforeOrder: ['FAQ BEFORE ORDER'],
  faqAfterOrder: ['FAQ AFTER ORDER'],
  voucherExpirationDate: ['VALIDITE', 'VOUCHER EXPIRATION DATE'],
  category: ['CATEGORIE', 'CATEGORY'],
  discount: ['DISCOUNT', 'DISCOUNT (%) RECOMMANDE', 'Discount (%) recommandé'],
  faqStep: ['ETAPE', 'STEP'],
  width: ['Width', 'Largeur'],
  height: ['Height', 'Hauteur'],
  length: ['Length', 'Longueur'],
  product_variant_should_be_included: [
    'Inclure ce produit dans la vente ?',
    'Include this product in the sale?',
  ],
  units_per_pack: ['COLISAGE', 'UNITS PER PACK', 'PCB'],
} satisfies Record<string, string[]>;

export const mappingSalesPurposes = {
  FR: Object.values(smartOfferValues).flat(),
  EN: Object.values(smartOfferValues).flat(),
  US: Object.values(smartOfferValues).flat(),
} as const;

/**
 * Champs SO absents du template CSVBox : une colonne du fichier à leur nom n'est
 * jamais mappée, et l'exclure de l'e-merch la ferait perdre. Elle reste donc de
 * l'e-merch (catégories, genre, poids, dimensions, colisage).
 */
const soFieldsKeptAsEmerch = new Set<keyof typeof smartOfferValues>([
  'category',
  'gender',
  'weight',
  'width',
  'height',
  'length',
  'units_per_pack',
]);

const smartOfferValuesWhitelist = new Set(
  Object.entries(smartOfferValues)
    .filter(
      ([field]) =>
        !soFieldsKeptAsEmerch.has(field as keyof typeof smartOfferValues)
    )
    .flatMap(([, columns]) => columns)
    .map((column) => normalizeColumnName(column))
);

/**
 * Root prefixes of columns that are NOT e-merchandising data.
 * A column is excluded if its normalized form starts with any of these prefixes.
 * This avoids listing every variant (e.g. "Tva", "TVA (%)", "Tva (à modifier si besoin)").
 */
const emerchBlacklistPrefixes = [
  'position',
  'classement',
  // `type` seul (colonne interne) est exclu par nom exact, cf. emerchBlacklistNames :
  // un préfixe couperait « Type de roues », « Type d'article »…
  'type de produit',
  'type cms',
  'type variation',
  'id deal',
  'issue',
  'inclus',
  'potentiel',
  'tva',
  'nom produit',
  'seo',
  'product category',
  'variant sku',
  'variant id',
  'prix publique',
  'prix de retrocession',
  'frais de livraison',
  'mondial relay',
  'offre',
  'franco',
  'prix public',
  'prix choose',
  'date',
  'cgu',
  'etape',
  'step',
  'nom ',
  'gtin',
  'sku',
  'url',
  'description',
  'marge',
  'discount',
  // Ajouts AUTO-1768, tirés des clés `smartOffer` réellement stockées : sans la
  // modale, plus personne ne décoche ces colonnes. Catégories et poids restent
  // de l'e-merch.
  // Artefacts techniques
  'unmapped data',
  'empty header',
  'undefined',
  'custom field',
  // Images non reprises par l'import (sauf liens Drive, cf. columnHasDriveLinks)
  'image',
  'photo ',
  'photos',
  'visuel',
  'lien visuel',
  'apercu',
  'swatch',
  'main image',
  // Prix, marge, remise
  'prix',
  'remise',
  'reduction',
  'escompte',
  'margin',
  'negociated',
  'taux de marge',
  'pvp',
  'pa ',
  'ca ',
  'cession',
  'choose',
  'ancien',
  'nouveau ',
  'nv ',
  'new discount',
  'montant pourcentage',
  'ecart',
  'difference',
  'currency',
  'vat',
  'french',
  'minimum',
  'conter proposal',
  'counter',
  // Logistique et stock
  'frais de port',
  'delai',
  'point relai',
  'transporteur',
  'livraison',
  'stock',
  'current stock',
  'alerte',
  'depot',
  'magasin',
  'emplacement',
  'code',
  'variant ',
  // Ventes et pilotage
  'best',
  'bs ',
  'quantite',
  'quantity',
  'qt ',
  'qq ',
  'ventes',
  'click',
  'top sizes',
  'reco',
  'completeness',
  'status',
  // Identifiants
  'id ',
  'identifiant',
  'key id',
  'deal',
  'parent',
  'base sku',
  'ref',
  'ean',
  'enecy',
  'under article',
  'included',
  'product ',
  // Doublons de champs SO
  'title',
  'designation',
  'descriptif',
  'option',
  // Échanges internes
  'retour marque',
  'commentaire',
  'message',
  'langue',
  // Ré-imports d'un export SO : seules les sous-colonnes de bruit, pour garder
  // matière et composition
  'so information image',
  'so information prix',
  'so information remise',
  'so information ean',
  'so information ref',
  'so information quantite',
  'so information check prix',
  'so information option',
  'so information availability',
  'so information unmapped data',
  // Ajouts après QA sur 36 imports prod (octobre 2026)
  'qte ',
  'reruns',
  'taux de conversion',
  'clics',
  'gmv',
  'last op',
  'diff',
  'contre proposition',
  'hs code',
  '%',
  'repere',
  'fdp',
  'decote',
  'final price',
  'price',
  'retrocession',
  'new b2b',
  'delivery',
  'comment',
  'sortie',
  'inventory',
  'liens visuel',
];

/** Noms exacts (normalisés) exclus, quand un préfixe couperait des colonnes utiles. */
const emerchBlacklistNames = new Set(['type', 'nom']);

/**
 * Normalize a column name for robust comparison:
 * - trim whitespace
 * - strip accents (NFD + remove combining marks)
 * - lowercase
 * - strip parenthetical content: "Tva (à modifier)" → "Tva"
 * - replace underscores, hyphens, and punctuation with spaces
 * - collapse multiple spaces into one
 */
function normalizeColumnName(value: string) {
  return value
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\(.*?\)/g, '')
    .replace(/[_\-.,;:!?/\\*]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function mediaColumnsTakenOverByImport(
  columns: string[],
  rows: Record<string, unknown>[]
): Set<string> {
  const cells = rows.map((row) => {
    const unmapped = row._unmapped_data as Record<string, unknown> | undefined;
    return columns.map((column) => {
      const value = unmapped?.[column];
      return typeof value === 'string' ? value : '';
    });
  });
  const detection = detectMediaColumns({ headers: columns, rows: cells });
  return new Set(
    mediaColumnsTakenOver(detection).map((column) => column.header)
  );
}

/**
 * Returns true if the column holds at least one Google Drive link. The import
 * does not take Drive file links over as images, so such a column goes to the
 * e-merch even when its name is blacklisted (e.g. "Image 1").
 */
function columnHasDriveLinks(
  rows: Record<string, unknown>[],
  column: string
): boolean {
  return rows.some((row) => {
    const unmapped = row._unmapped_data as Record<string, unknown> | undefined;
    const value = unmapped?.[column];
    return (
      typeof value === 'string' &&
      extractUrls(value).some((url) => classifyUrl(url).startsWith('drive'))
    );
  });
}

function matchesBlacklistPrefix(normalizedColumn: string) {
  return (
    emerchBlacklistNames.has(normalizedColumn) ||
    emerchBlacklistPrefixes.some((prefix) =>
      normalizedColumn.startsWith(prefix)
    )
  );
}

/**
 * Returns true if at least one row has a non-empty value for the given column
 * inside `_unmapped_data`.
 */
function columnHasData(
  rows: Record<string, unknown>[],
  column: string
): boolean {
  return rows.some((row) => {
    const unmapped = row._unmapped_data as Record<string, unknown> | undefined;
    const value = unmapped?.[column];
    return value !== undefined && value !== null && String(value).trim() !== '';
  });
}

export function detectEmerchColumns(
  ignoredColumns: string[] = [],
  rows: Record<string, unknown>[] = []
) {
  const seenColumns = new Set<string>();
  const takenOverByImport = mediaColumnsTakenOverByImport(ignoredColumns, rows);

  return ignoredColumns.filter((column) => {
    const normalizedColumn = normalizeColumnName(column);

    if (!normalizedColumn) {
      return false;
    }

    if (smartOfferValuesWhitelist.has(normalizedColumn)) {
      return false;
    }

    if (takenOverByImport.has(column)) {
      return false;
    }

    if (
      matchesBlacklistPrefix(normalizedColumn) &&
      !columnHasDriveLinks(rows, column)
    ) {
      return false;
    }

    if (seenColumns.has(normalizedColumn)) {
      return false;
    }

    // Skip columns where no row has any actual data
    if (rows.length > 0 && !columnHasData(rows, column)) {
      return false;
    }

    seenColumns.add(normalizedColumn);
    return true;
  });
}
